import { Query } from './query';
import { Page } from '../components/paginator/page.type';
import { SortedField, Sorting } from './sorting';
import { Filter, Filters } from './filter';
import { BehaviorSubject, Subject, filter, finalize, Observable, timer, withLatestFrom } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export class PagedSearch {
    currentPage: number;
    perPage: number;
    filters: Filters;

    constructor(
        currentPage: number,
        perPage: number,
        filters: Filters | null = null,
    ) {
        this.currentPage = currentPage > 0 ? currentPage : 1;
        this.perPage = perPage > 0 ? perPage : 10;
        this.filters = filters || new Filters();
    }

    toQueryString(): string {
        const params: Record<string, string> = {};

        if (this.currentPage > 0) {
            params['current_page'] = String(this.currentPage);
        }

        if (this.perPage > 0) {
            params['per_page'] = String(this.perPage);
        }

        if (this.filters?.size() > 0) {
            const filterString = this.filters.toQueryString(); // ex: "status=active&role=admin"
            const baseQuery = new URLSearchParams(params).toString();
            return `?${baseQuery}&${filterString}`;
        }

        const query = new URLSearchParams(params).toString();
        return query ? `?${query}` : '';
    }
}

/**
 * Define uma consulta paginada.
 * Simplifica a realização de consultas a APIs que usam o padrão
 * de consulta:
 *
 *    /endpoint?filter1=teste&filter2=teste2&sort=name,asc&per_page=10&current_page=1
 *
 * As consultas ao Endpoint são criadas dinamicamente, usando o padrão acima.
 *
 * @param T Tipo dos dados que serão retornados na consulta.
 */
export class PagedQuery<D> implements Query<Page<D>> {
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    private readonly loadingObservable = this.loadingSubject.asObservable();
    private sorting: Sorting;
    private currentPage: number = 1;
    public pageSize: number = 5;
    private total: number = 0;
    private autoRefresh: number | null = null;
    private filters: Filters = new Filters();
    private readonly dataSubject = new BehaviorSubject<Page<D>>({
        data: [],
        currentPage: this.currentPage,
        total: 0,
        pageSize: this.pageSize,
    } as Page<D>);
    private readonly dataObservable = this.dataSubject.asObservable();
    private readonly errorSubject = new Subject<HttpErrorResponse>();
    private readonly errorObservable = this.errorSubject.asObservable();
    private readonly endpoint: string;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
        readonly baseUrl: string,
        pageSize: number,
        readonly path: string,
        readonly preFetchFirstPage: boolean = false,
        sorting: Sorting = new Sorting(),
        filters: Filters = new Filters(),
        autoRefresh: number | null = null,
    ) {
        this.endpoint = `${baseUrl}/${path}`;
        this.pageSize = pageSize;
        this.sorting = sorting;
        this.filters = filters;
        this.baseUrl = baseUrl;
        this.autoRefresh = autoRefresh;

        if (preFetchFirstPage) {
            this.fetch();
        }

        if (this.autoRefresh && this.autoRefresh >= 2000) {
            this.handleAutoRefresh();
        }
    }

    onReady(): Observable<Page<D>> {
        return this.dataObservable;
    }

    onError(): Observable<HttpErrorResponse> {
        return this.errorObservable;
    }

    isLoading(): Observable<boolean> {
        return this.loadingObservable;
    }

    /**
     * Adiciona um campo de ordenação à consulta.
     *
     * @param field O campo a ser ordenado.
     * @param direction A direção da ordenação (asc ou desc).
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    sortBy(
        field: string,
        direction: 'asc' | 'desc' | '' | undefined,
    ): PagedQuery<D> {
        this.sorting.addField(
            new SortedField(
                field,
                direction === 'asc' ? Sorting.ASC : Sorting.DESC,
            ),
        );
        return this;
    }

    /**
     * Adiciona um filtro à consulta.
     *
     * @param name O nome do filtro.
     * @param value O valor do filtro.
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    filterBy(name: string, value: any): PagedQuery<D> {
        this.currentPage = 1;
        this.filters.add(name, value);
        return this;
    }

    /**
     * Limpa os campos de ordenação da consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearSorting(): PagedQuery<D> {
        this.sorting = new Sorting();
        return this;
    }

    /**
     * Limpa os filtros aplicados na consulta.
     *
     * @return Instância do PagedQuery para encadeamento de chamadas.
     */
    clearFilters(): PagedQuery<D> {
        this.filters = new Filters();
        return this;
    }

    /**
     * Avança para a próxima página da consulta.
     *
     * @returns void
     */
    next(): void {
        this.currentPage++;
        this.fetch();
    }

    /**
     * Retorna à página anterior da consulta.
     *
     * @returns void
     */
    previous(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.fetch();
        }
    }

    /**
     * Vai para a página especificada da consulta.
     *
     * @param page Número da página para a qual ir.
     * @returns void
     */
    goToPage(page: number): void {
        if (page > 0) {
            this.currentPage = page;
            this.fetch();
        }
    }

    /**
     * Altera a quantidade de itens por página e recarrega a partir da 1ª
     * página. Ignora valores inválidos (≤ 0) ou iguais ao atual.
     *
     * @param pageSize Novo tamanho de página.
     * @returns void
     */
    changePageSize(pageSize: number): void {
        if (pageSize <= 0 || pageSize === this.pageSize) {
            return;
        }
        this.pageSize = pageSize;
        this.currentPage = 1;
        this.fetch();
    }

    /**
     * Realiza a busca dos dados na API.
     *
     * Constrói a consulta paginada com base nos filtros,
     * ordenamentos e página atual, e faz uma requisição HTTP GET
     * para o endpoint configurado.
     *
     * Caso uma requisição já esteja em andamento, a requisição
     * será ignorada para evitar múltiplas requisições simultâneas.
     *
     * @returns void
     */
    fetch(): void {
        // Evita múltiplas requisições simultâneas.
        if (this.loadingSubject.value) {
            return;
        }

        const search = new PagedSearch(
            this.currentPage,
            this.pageSize,
            this.filters,
        );
        const sortQueryString = this.sorting.toQueryString();
        this.loadingSubject.next(true);

        this.httpClient
            .get<Page<D>>(
                `${this.endpoint}/${search.toQueryString()}${sortQueryString ? `&${sortQueryString}` : ''}`,
            )
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.loadingSubject.next(false)),
            )
            .subscribe({
                next: (value: Page<D>) => {
                    this.total = value.total;
                    this.handleCurrentPage(value);
                    this.dataSubject.next(value);
                },
                error: (error: HttpErrorResponse) => {
                    console.error(error);
                    this.errorSubject.next(error);
                },
            });
    }

    /**
     * Obtém todos os registros contidos em uma única página.
     *
     */
    all(): void {
        this.currentPage = -1;
        this.pageSize = -1;
        this.fetch();
    }

    /**
     * Trata a página atual recebida da API.
     * Atualiza a página atual se a página recebida contiver dados.
     *
     * @param page A página recebida da API.
     * @returns void
     */
    private handleCurrentPage(page: Page<D>): void {
        if (page && page.data && page.data.length > 0) {
            this.currentPage = page.currentPage;
        }
    }

    /**
     * Trata o processo de autoRefresh, criando um interval com o RxJs.
     *
     * @private
     */
    private handleAutoRefresh(): void {
        timer(this.autoRefresh!, this.autoRefresh!)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                withLatestFrom(this.loadingObservable),
                filter(([, isLoading]) => !isLoading),
            )
            .subscribe(() => this.fetch());
    }
}

/**
 * Builder para simplificar a criação de objetos PagedQuery.
 *
 * Ao ser injetado em um componente, já leva junto o httpClient e destroyRef
 * necessários para o funcionamento do PagedQuery criada.
 */
@Injectable({providedIn: 'root'})
export class PagedQueryBuilder {
    private aBaseUrl: string | null = null;
    private aPath: string | null = null;
    private aPageSize: number | null = null;
    private aPreFetchFirstPage: boolean = false;
    private aSortedFields: SortedField[] = [];
    private aFilters: Filter[] = [];
    private aAutoRefresh: number | null = null;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly destroyRef: DestroyRef,
    ) {}

    /**
     * Define o caminho da API para a consulta paginada.
     *
     * @param path Caminho da API.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    path(path: string): PagedQueryBuilder {
        this.aPath = path;
        return this;
    }

    /**
     * Define a URL base para a consulta paginada.
     *
     * @param baseUrl URL base da API.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    baseUrl(baseUrl: string): PagedQueryBuilder {
        this.aBaseUrl = baseUrl;
        return this;
    }

    /**
     * Define o tamanho da página para a consulta paginada.
     *
     * @param pageSize Tamanho da página.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    pageSize(pageSize: number): PagedQueryBuilder {
        this.aPageSize = pageSize;
        return this;
    }

    /**
     * Define se a primeira página deve ser pré-carregada.
     *
     * @param preFetchFirstPage Indica se a primeira página deve ser pré-carregada.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    preFetchFirstPage(preFetchFirstPage: boolean): PagedQueryBuilder {
        this.aPreFetchFirstPage = preFetchFirstPage;
        return this;
    }

    /**
     * Adiciona um campo para ordenação na consulta paginada.
     *
     * @param field Campo a ser ordenado.
     * @param direction Direção da ordenação (asc ou desc).
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    sortBy(field: string, direction: 'asc' | 'desc'): PagedQueryBuilder {
        this.aSortedFields.push(new SortedField(field, direction));
        return this;
    }

    /**
     * Adiciona um filtro à consulta paginada.
     *
     * @param name Nome do filtro.
     * @param value Valor do filtro.
     * @return Instância do PagedQueryBuilder para encadeamento de chamadas.
     */
    filterBy(name: string, value: string): PagedQueryBuilder {
        this.aFilters.push(new Filter(name, value));
        return this;
    }

    /**
     * Define o intervalo de autoatualização da consulta paginada.
     *
     * @param interval Intervalo em milissegundos para auto-atualização.
     */
    autoRefresh(interval: number): PagedQueryBuilder {
        this.aAutoRefresh = interval;
        return this;
    }

    /**
     * Constrói e retorna uma instância de PagedQuery com as configurações definidas.
     *
     * @returns Instância de PagedQuery configurada.
     */
    build<T>(): PagedQuery<T> {
        if (!this.aPath || !this.aPageSize || !this.aBaseUrl) {
            throw new Error(
                'Os parâmetros BaseUrl, Path e PageSize são obrigatórios.',
            );
        }

        return new PagedQuery<T>(
            this.httpClient,
            this.destroyRef,
            this.aBaseUrl,
            this.aPageSize,
            this.aPath,
            this.aPreFetchFirstPage,
            new Sorting(this.aSortedFields),
            new Filters(this.aFilters),
            this.aAutoRefresh,
        );
    }
}
